import { withToPar } from '../../components/toPar'
import { useEffect, useRef } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  DERIVED_METRICS,
  METRICS,
  api,
  type Metric,
  type StatBucket,
  type StatResult,
} from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { DistributionBar, DistributionLegend } from '../../components/DistributionBar'
import { useApi } from '../../components/useApi'
import { useSeason } from './useSeason'
import { usePlayerPaths } from '../../components/playerPaths'

/**
 * The statistics view — the parity surface (FR-039d through FR-039h).
 *
 * # Coverage is beside every number, not in a footnote
 *
 * FR-039r is the requirement this view is built around, and it is not a
 * nicety. Across the league's 115 scored rounds, putts appear on 109, fairways
 * on 93, greens on 83 and **sand saves on 17**. A sand-save percentage rendered
 * as a season statistic would describe a sixth of the season while looking like
 * all of it — so every figure carries the rounds it was computed from, and a
 * measure that covers only part of what was played says so in words rather than
 * leaving the reader to compare two numbers.
 *
 * # A measure with nothing recorded is MARKED, never shown as zero
 *
 * FR-039s. "0.0 putts per hole" and "nobody recorded putts" are the same number
 * and opposite facts, and the first one is the one a reader believes. The
 * server sends `no_data`; this view renders a sentence, not a figure.
 *
 * # Net and gross, all-time and season, are controls and not separate pages
 *
 * FR-039f and FR-039g apply to EVERY statistic, so they sit above the metric
 * rather than inside it — there is no statistic here that supports one basis
 * and not the other, because the server has none.
 */
export function Stats() {
  const { seasonId } = useParams()
  const season = useSeason(seasonId)
  const [params, setParams] = useSearchParams()

  const metric = (params.get('metric') ?? 'scoring_average') as Metric
  const basis = params.get('basis') === 'net' ? 'net' : 'gross'
  // **The query carries a NAME too, for the same reason the path does.** A
  // statistics slice is a URL precisely so it can be shared, and
  // `?player_id=00000000-...` is no more shareable in a query than in a path.
  // Either form resolves, so links already sent round keep working.
  const { resolve, slugFor, loaded } = usePlayerPaths()
  const playerParam = params.get('player_id') ?? ''
  // Until the roster has loaded there is nothing to resolve a name against, and
  // sending the slug to the API would ask it for a player it has never heard
  // of. A junk value that resolves to nobody is passed through as it always
  // was, so the server stays the one that decides it is junk.
  const waitingForRoster = playerParam !== '' && !loaded
  const playerId = playerParam ? (resolve(playerParam) ?? playerParam) : ''
  const courseKey = params.get('course_key') ?? ''
  // All-time is the default, because four years of history is the thing worth
  // keeping (FR-039g). A season is something you ask for, and asking for it is
  // a different URL rather than a control — so a slice of the statistics is
  // shareable, and this view keeps the property every public view is asserted
  // to have: nothing on it changes anything (FR-047c).
  //
  // **Every season is offered, by its id.** The first version offered only
  // "this season", which was all there was; once 2023-24 began, 2022-23's
  // statistics were reachable by nothing on the page. A link names the season
  // by id so a shared URL still means the same season next year. `current` is
  // still read, because older links carry it.
  const seasons = useApi((s) => api.seasons(s), [])
  const periodParam = params.get('season_id') ?? ''
  const scopedSeason =
    periodParam === 'current'
      ? season.state === 'ready'
        ? season.data.id
        : undefined
      : Number(periodParam) || undefined
  const scoped = periodParam !== ''

  const periodName =
    scopedSeason && seasons.state === 'ready'
      ? (seasons.data.find((x) => x.id === scopedSeason)?.name ?? 'Season')
      : 'All time'

  // Close an open menu when the reader clicks anywhere outside it, and keep one
  // menu open at a time. Opening needs no script -- this only tidies up.
  const menusRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const close = (event: MouseEvent) => {
      const root = menusRef.current
      if (!root) return
      for (const menu of root.querySelectorAll<HTMLDetailsElement>('details[open]')) {
        if (!menu.contains(event.target as Node)) menu.open = false
      }
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])

  const result = useApi(
    waitingForRoster
      ? null
      : (s) => api.stats({ metric, basis, seasonId: scopedSeason, playerId, courseKey }, s),
    [metric, basis, scopedSeason ?? 0, playerId, courseKey, waitingForRoster],
  )
  // Every control is a link that carries the OTHER two choices with it, so
  // switching metric keeps your basis and period rather than resetting them.
  const to = (next: Record<string, string>) => {
    const q = new URLSearchParams({
      metric,
      basis,
      ...(scopedSeason ? { season_id: String(scopedSeason) } : {}),
      ...(playerId ? { player_id: slugFor(playerId) ?? playerId } : {}),
      ...(courseKey ? { course_key: courseKey } : {}),
      ...next,
    })
    for (const key of ['season_id', 'player_id', 'course_key']) {
      if (!q.get(key)) q.delete(key)
    }
    return `?${q.toString()}`
  }
  void setParams

  return (
    <section className="stats" data-testid="stats">
      <h2>Statistics</h2>

      {/* **Filters as a row of dropdowns above the statistic.** Stacked rows of
          pills put the answer half a screen down, and a sidebar of every option
          scrolled on a desktop. Each filter is now one compact button naming its
          current value, opening a menu.

          **The menus are LINKS in a disclosure, not a <select>.** Every public
          view is asserted to carry no form control (FR-047c), and a link gives
          each slice its own shareable URL and a working back button -- neither
          of which a select that navigates on change has. `<details>` needs no
          script to open; the one effect below only closes a menu the reader
          clicked away from. Keyed on the query, so choosing an option lands on
          a page with every menu closed. */}
      <div className="stats-filters" key={params.toString()} ref={menusRef}>
        <FilterMenu title="Statistic" value={metricLabel(metric)} testId="menu-metric" wide>
          {METRIC_GROUPS.map((group) => (
            <FilterGroup key={group.title} title={group.title}>
              {group.metrics.map((m) => (
                <FilterLink
                  key={m}
                  to={to({ metric: m })}
                  current={m === metric}
                  currentKind="page"
                  testId={`metric-${m}`}
                >
                  {metricLabel(m)}
                </FilterLink>
              ))}
            </FilterGroup>
          ))}
        </FilterMenu>

        <FilterMenu
          title="Score"
          value={basis === 'gross' ? 'Gross' : 'Net'}
          label="Basis"
          testId="menu-basis"
        >
          <FilterGroup>
            {(['gross', 'net'] as const).map((b) => (
              <FilterLink key={b} to={to({ basis: b })} current={b === basis} testId={`basis-${b}`}>
                {b === 'gross' ? 'Gross' : 'Net'}
              </FilterLink>
            ))}
          </FilterGroup>
        </FilterMenu>

        <FilterMenu title="Period" value={periodName} testId="menu-period">
          <FilterGroup>
            <FilterLink to={to({ season_id: '' })} current={!scoped} testId="period-all">
              All time
            </FilterLink>
            {seasons.state === 'ready' &&
              seasons.data.map((option) => (
                <FilterLink
                  key={option.id}
                  to={to({ season_id: String(option.id) })}
                  current={option.id === scopedSeason}
                  testId={`period-season-${option.id}`}
                >
                  {option.name}
                </FilterLink>
              ))}
          </FilterGroup>
        </FilterMenu>

        {result.state === 'ready' && (
          /* FR-039h: by player and by course. Rendered from what the PERIOD
             holds, so every option leads somewhere — a player who sat the
             season out is not offered as a filter. */
          <Filters
            available={result.data.available}
            playerId={playerId}
            courseKey={courseKey}
            to={to}
          />
        )}
      </div>

      {result.state === 'loading' && <Loading what="the statistic" />}
      {result.state === 'error' && <Failed what="The statistic" error={result.error} />}
      {result.state === 'ready' && <StatPanel result={result.data} metric={metric} />}
    </section>
  )
}

/**
 * One filter as a dropdown: a summary naming the filter and its current value,
 * opening a panel of links.
 */
function FilterMenu({
  title,
  value,
  label,
  testId,
  wide,
  scroll,
  children,
}: {
  title: string
  value: string
  label?: string
  testId?: string
  wide?: boolean
  scroll?: boolean
  children: React.ReactNode
}) {
  return (
    <details className="filter-menu" aria-label={label ?? title}>
      <summary data-testid={testId}>
        <span className="filter-menu-title">{title}</span>{' '}
        <span className="filter-menu-value">{value}</span>
      </summary>
      <div
        className={`filter-menu-panel${wide ? ' filter-menu-wide' : ''}${
          scroll ? ' filter-menu-scroll' : ''
        }`}
      >
        {children}
      </div>
    </details>
  )
}

/**
 * The statistics in the groups a golfer asks about them in. Every metric the
 * server offers appears exactly once; one it adds later that is not placed here
 * lands in "More" rather than disappearing.
 */
const METRIC_GROUPS: { title: string; metrics: Metric[] }[] = (() => {
  const groups: { title: string; metrics: Metric[] }[] = [
    {
      title: 'Scoring',
      metrics: [
        'score_history',
        'scoring_average',
        'score_distribution',
        'score_distribution_by_player',
        'best_round',
        'worst_round',
        'potential_best_round',
      ] as Metric[],
    },
    { title: 'By hole', metrics: ['par3', 'par4', 'par5', 'hole_difficulty'] as Metric[] },
    {
      title: 'Putting',
      metrics: ['putting_average', 'putting_distribution', 'putts'] as Metric[],
    },
    {
      title: 'Tee to green',
      metrics: ['fairways', 'greens_in_regulation', 'sand_saves', 'driving_distance'] as Metric[],
    },
  ]
  const placed = new Set(groups.flatMap((g) => g.metrics))
  const more = METRICS.filter((m) => !placed.has(m))
  const known = new Set<string>(METRICS)
  return [
    ...groups.map((g) => ({ ...g, metrics: g.metrics.filter((m) => known.has(m)) })),
    ...(more.length > 0 ? [{ title: 'More', metrics: more }] : []),
  ]
})()

/** One group of options inside a menu, titled where the menu holds several. */
function FilterGroup({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="filter-group" role="group" aria-label={title}>
      {title && <h3 className="filter-title">{title}</h3>}
      <ul className="filter-options">{children}</ul>
    </div>
  )
}

/** One option: a link, marked current where it is the slice being shown. */
function FilterLink({
  to,
  current,
  currentKind = 'true',
  testId,
  children,
}: {
  to: string
  current: boolean
  currentKind?: 'page' | 'true'
  testId?: string
  children: React.ReactNode
}) {
  return (
    <li>
      <Link
        to={to}
        className={current ? 'current' : undefined}
        aria-current={current ? currentKind : undefined}
        data-testid={testId}
      >
        {children}
      </Link>
    </li>
  )
}

/**
 * The diverging ramp for score distribution: one hue per arm, intensity rising
 * away from the middle, a near-neutral at par.
 *
 * **Every step was run through the data-viz validator** against this app's own
 * surface rather than eyeballed — each arm as a one-hue ordinal ramp, and the
 * adjacent pairs for colourblind separation and the normal-vision floor. The
 * light values pass at ΔE 14.5 (protan) and 16.6 (normal). Dark is SELECTED
 * against the dark surface, not flipped, and passes at 11.9 and 17.2.
 *
 * Order matches the server's bucket order: albatross → double bogey or worse.
 */
const SCORE_RAMP = ['#14513a', '#2a8557', '#78bd9c', '#dfe2dd', '#e08e8a', '#a03232']

/**
 * Putts have no neutral — one putt is not the middle of anything — so this is a
 * SEQUENTIAL ramp rather than a diverging one: one hue, more-is-darker. The
 * steps are the reference palette's published blue ordinal steps, whose light
 * end is the documented minimum that still clears the surface.
 */
const PUTTS_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281']

// **These are the LIGHT steps, and they are the only steps, because the app is
// light.** `styles.css` declares `color-scheme: light` on the root: there is no
// dark theme here, and a chart that followed the OS preference would be the one
// dark element on a white page. Found by screenshotting it in a dark-scheme
// browser — a near-black "par" segment sitting on the paper background.
//
// The dark ramps were derived and validated alongside these and are recorded
// here so the work is not lost if a dark theme arrives:
//
//	scores: #b9e6cd #5cb184 #2a6f4c #383835 #a8433e #ef9995
//	putts:  #cde2fb #9ec5f4 #6da7ec #3987e5 #184f95

function Filters({
  available,
  playerId,
  courseKey,
  to,
}: {
  available: StatResult['available']
  playerId: string
  courseKey: string
  to: (next: Record<string, string>) => string
}) {
  const { slugFor } = usePlayerPaths()
  const player = available.players.find((p) => p.player_id === playerId)
  return (
    <>
      {available.players.length > 1 && (
        <FilterMenu title="Player" value={player?.display_name ?? 'Everyone'} testId="menu-player">
          <FilterGroup>
            <FilterLink to={to({ player_id: '' })} current={!playerId} testId="player-all">
              Everyone
            </FilterLink>
            {available.players.map((p) => (
              <FilterLink
                key={p.player_id}
                to={to({ player_id: slugFor(p.player_id) ?? p.player_id })}
                current={p.player_id === playerId}
                testId={`player-${p.player_id}`}
              >
                {p.display_name}
              </FilterLink>
            ))}
          </FilterGroup>
        </FilterMenu>
      )}
      {available.courses.length > 1 && (
        <FilterMenu title="Course" value={courseKey || 'Every course'} testId="menu-course" scroll>
          <FilterGroup>
            <FilterLink to={to({ course_key: '' })} current={!courseKey} testId="course-all">
              Every course
            </FilterLink>
            {available.courses.map((c) => (
              <FilterLink key={c} to={to({ course_key: c })} current={c === courseKey}>
                {c}
              </FilterLink>
            ))}
          </FilterGroup>
        </FilterMenu>
      )}
    </>
  )
}

/**
 * Every player's score distribution, laid against each other.
 *
 * **One legend, one bar per player, all the same width** -- which is why this is
 * a stack of bars and not a row of pies: two bars line up, two rings cannot.
 * Ordered by the share of holes at par or better, best first, so the comparison
 * reads top to bottom; each row keeps its own round count, because a player
 * with four rounds is not compared with the confidence of one with seventeen.
 * The table underneath carries every number the bars only show.
 *
 * The share is taken from the COUNTS, which are integers, as the bar itself
 * does -- the server's per-bucket percentages are decimal strings and are
 * printed, never added up here.
 */
/**
 * A percentage string to one decimal place, as TEXT: the server drops a
 * trailing zero ("55" beside "11.7"), and padding the string -- rather than
 * parsing a decimal -- keeps the browser out of the arithmetic.
 */
function oneDecimal(value: string): string {
  return value.includes('.') ? value : `${value}.0`
}

function DistributionComparison({ result }: { result: StatResult }) {
  const { pathFor } = usePlayerPaths()
  const pivot = result.breakdown.findIndex((b) => b.key === 'par')
  const parOrBetter = (buckets: StatBucket[]) =>
    buckets.slice(0, pivot + 1).reduce((n, b) => n + b.count, 0)
  const rows = result.series
    .filter((s) => !s.no_data && s.based_on_holes > 0)
    .map((s) => ({ ...s, better: parOrBetter(s.breakdown) }))
    .sort((a, b) => b.better * a.based_on_holes - a.better * b.based_on_holes)

  if (rows.length === 0) return null
  const pct = (n: number, of: number) => ((n / of) * 100).toFixed(1)

  return (
    <div className="distribution-comparison" data-testid="distribution-comparison">
      <DistributionLegend buckets={result.breakdown} palette={SCORE_RAMP} />
      <ol className="comparison-rows">
        {rows.map((s) => (
          <li
            key={s.player_id}
            className="comparison-row"
            data-testid={`comparison-${s.player_id}`}
          >
            <div className="comparison-who">
              <Link to={pathFor(s.player_id)}>{s.display_name}</Link>
              <span className="note">
                {s.based_on_rounds} {s.based_on_rounds === 1 ? 'round' : 'rounds'}
              </span>
            </div>
            <DistributionBar
              compact
              buckets={s.breakdown}
              total={s.based_on_holes}
              palette={SCORE_RAMP}
              pivotAfter="par"
              caption={`${s.display_name}: ${pct(s.better, s.based_on_holes)}% of ${
                s.based_on_holes
              } holes at par or better`}
            />
            <div className="comparison-share">
              <strong>{pct(s.better, s.based_on_holes)}%</strong> par or better
            </div>
          </li>
        ))}
      </ol>

      <div className="scroll-x">
        <table className="table stat-table" data-testid="stat-breakdown">
          <thead>
            <tr>
              <th scope="col">Player</th>
              {result.breakdown.map((b) => (
                <th key={b.key} scope="col" className="numeric">
                  {b.label}
                </th>
              ))}
              <th scope="col" className="numeric">
                Rounds
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.player_id}>
                <th scope="row">{s.display_name}</th>
                {s.breakdown.map((b) => (
                  <td key={b.key} className="numeric" title={`${b.count} holes`}>
                    {b.value === null ? '—' : `${oneDecimal(b.value)}%`}
                  </td>
                ))}
                <td className="numeric">{s.based_on_rounds}</td>
              </tr>
            ))}
            <tr className="comparison-total">
              <th scope="row">Everyone</th>
              {result.breakdown.map((b) => (
                <td key={b.key} className="numeric" title={`${b.count} holes`}>
                  {b.value === null ? '—' : `${oneDecimal(b.value)}%`}
                </td>
              ))}
              <td className="numeric">{result.based_on_rounds}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function StatPanel({ result, metric }: { result: StatResult; metric: Metric }) {
  // FR-039s: a sentence, not a figure. Rendering the zero the arithmetic would
  // have produced is the single most misleading thing this page could do.
  if (result.no_data) {
    return (
      <div data-testid="stat-no-data">
        <h3>{metricLabel(metric)}</h3>
        <Empty>
          Nothing recorded for this period.{' '}
          {DERIVED_METRICS.includes(metric)
            ? 'No round has been completed here yet.'
            : 'This measure is optional on a submitted score, and nobody sent it — which is not the same as a zero.'}
        </Empty>
      </div>
    )
  }

  return (
    <div>
      <h3>{metricLabel(metric)}</h3>

      {result.value !== null && (
        <p className="stat-headline" data-testid="stat-value">
          {/* A decimal string, printed. The server does the arithmetic
              (Constitution III) and parsing it here would reintroduce in the
              browser exactly the error the scoring core avoids. */}
          <strong>{result.value}</strong>
          <span className="stat-unit"> {unitFor(metric)}</span>
        </p>
      )}

      <Coverage result={result} metric={metric} />

      {/* A chart only where the data's job calls for one. Two distributions are
          ordered part-to-whole and get a stacked bar; everything else here is a
          single figure or a ranked table, and a chart of one number is a
          number with decoration. */}
      {metric === 'score_distribution' && (
        <DistributionBar
          buckets={result.breakdown}
          total={result.based_on_holes}
          palette={SCORE_RAMP}
          pivotAfter="par"
          pivotLabels={{ before: 'Par or better', after: 'Worse than par' }}
          caption={`Every hole of ${result.based_on_rounds} ${
            result.based_on_rounds === 1 ? 'round' : 'rounds'
          }, best to worst. Hover a segment for its count.`}
        />
      )}
      {metric === 'putting_distribution' && (
        <DistributionBar
          buckets={result.breakdown}
          total={result.based_on_holes}
          palette={PUTTS_RAMP}
          caption={`Every hole that reported putts, fewest to most. Hover a segment for its count.`}
        />
      )}

      {metric === 'score_distribution_by_player' && <DistributionComparison result={result} />}

      {result.breakdown.length > 0 && metric !== 'score_distribution_by_player' && (
        <div className="scroll-x">
          <table className="table stat-table" data-testid="stat-breakdown">
            <thead>
              <tr>
                <th scope="col">{breakdownHeading(metric)}</th>
                <th scope="col" className="numeric">
                  {countHeading(metric)}
                </th>
                <th scope="col" className="numeric">
                  {valueHeading(metric)}
                </th>
                <th scope="col">Where</th>
              </tr>
            </thead>
            <tbody>
              {result.breakdown.map((b, i) => (
                <tr key={`${b.key}-${i}`} data-testid={`stat-row-${b.key}`}>
                  <th scope="row">{b.label || b.key}</th>
                  <td className="numeric">{b.count}</td>
                  <td className="numeric">
                    {b.value === null
                      ? '—'
                      : b.to_par !== undefined
                        ? withToPar(b.value, b.to_par)
                        : b.value}
                  </td>
                  <td>
                    {b.event_id ? (
                      <Link to={`/events/${b.event_id}`}>
                        {b.date ?? `event ${b.event_id}`}
                        {b.course && ` · ${b.course}`}
                      </Link>
                    ) : (
                      (b.course ?? '')
                    )}
                    {b.display_name && <span className="stat-who"> {b.display_name}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * What the figure is based on (FR-039r, SC-013f).
 *
 * Rendered as a sentence rather than a pair of numbers, because "17 of 115"
 * only means something to a reader who already knows which is which.
 */
function Coverage({ result, metric }: { result: StatResult; metric: Metric }) {
  const derived = DERIVED_METRICS.includes(metric)
  return (
    <p
      className={`note stat-coverage${result.complete_coverage ? '' : ' note-warn'}`}
      data-testid="stat-coverage"
    >
      Based on <strong>{result.based_on_rounds}</strong>{' '}
      {result.based_on_rounds === 1 ? 'round' : 'rounds'} ({result.based_on_holes}{' '}
      {result.based_on_holes === 1 ? 'hole' : 'holes'}).
      {result.complete_coverage ? (
        derived ? (
          <>
            {' '}
            Every hole in the period counted — this statistic needs nothing a score does not carry.
          </>
        ) : (
          <> Reported on every hole in the period.</>
        )
      ) : (
        <>
          {' '}
          <strong>Not every round.</strong> The period holds {result.rounds_in_period}{' '}
          {result.rounds_in_period === 1 ? 'round' : 'rounds'} and {result.holes_in_period} holes;
          this measure was recorded on {result.based_on_holes} of them, so it describes part of the
          period rather than all of it.
        </>
      )}
    </p>
  )
}

function metricLabel(m: string): string {
  const labels: Record<string, string> = {
    score_history: 'Score history',
    score_distribution: 'Score distribution',
    score_distribution_by_player: 'Score distribution by player',
    scoring_average: 'Scoring average',
    best_round: 'Best round',
    worst_round: 'Worst round',
    potential_best_round: 'Potential best round',
    putting_average: 'Putting average',
    putting_distribution: 'Putting distribution',
    par3: 'Par 3s',
    par4: 'Par 4s',
    par5: 'Par 5s',
    hole_difficulty: 'Hole difficulty',
    fairways: 'Fairways hit',
    greens_in_regulation: 'Greens in regulation',
    putts: 'Putts',
    sand_saves: 'Sand saves',
    driving_distance: 'Driving distance',
  }
  return labels[m] ?? m.replace(/_/g, ' ')
}

function unitFor(m: string): string {
  switch (m) {
    case 'scoring_average':
    case 'putting_average':
      return 'per hole'
    case 'par3':
    case 'par4':
    case 'par5':
      return 'strokes over par, per hole'
    case 'fairways':
    case 'greens_in_regulation':
    case 'sand_saves':
      return '%'
    case 'driving_distance':
      return 'yards on average'
    default:
      return ''
  }
}

function breakdownHeading(m: string): string {
  switch (m) {
    case 'score_history':
      return 'Round'
    case 'hole_difficulty':
      return 'Hole'
    case 'potential_best_round':
      return 'Course'
    default:
      return 'Result'
  }
}

function countHeading(m: string): string {
  switch (m) {
    case 'score_history':
      return 'Holes'
    case 'hole_difficulty':
      return 'Played'
    default:
      return 'Count'
  }
}

function valueHeading(m: string): string {
  switch (m) {
    case 'score_distribution':
    case 'putting_distribution':
      return 'Share %'
    case 'hole_difficulty':
      return 'Over par'
    default:
      return 'Score'
  }
}
